import type { ForecastPeriod } from "@/lib/api/models/forecast";
import type { WeekArea, WeekTone } from "@/lib/api/models/week";
import { Label, Tiny } from "@/ui/Text";
import { planetsIn } from "./planets";

const PERIOD_NOUN: Readonly<Record<ForecastPeriod, string>> = { day: "day", week: "week", month: "month" };

function toneLabel(tone: WeekTone, period: ForecastPeriod): string {
  if (tone === "good") return `Good ${PERIOD_NOUN[period]}`;
  return tone === "careful" ? "Go gently" : "Steady";
}

/** The chart line under a reading's label: "Makara rashi · Dhanu lagna". */
export function SignsLine({ moonRashi, lagnaRashi }: { readonly moonRashi: string; readonly lagnaRashi: string }) {
  if (!moonRashi || !lagnaRashi) return null;
  return (
    <p className="mt-3 font-mono text-[12px] tracking-[0.08em] text-mid">
      {moonRashi} rashi · {lagnaRashi} lagna
    </p>
  );
}

/**
 * The days to use and the days to go slowly. They come first: they are the
 * one thing on the page a person can put straight into their calendar.
 */
export function DaysBlock({ best, careful }: { readonly best: readonly string[]; readonly careful: readonly string[] }) {
  if (best.length === 0 && careful.length === 0) return null;
  return (
    <div className="mt-5 border-y-[1.5px] border-ink py-4">
      <DaysList best={best} careful={careful} />
    </div>
  );
}

/** The same days, unframed, for a section that brings its own frame. */
export function DaysList({ best, careful }: { readonly best: readonly string[]; readonly careful: readonly string[] }) {
  return (
    <div className="grid grid-cols-2 gap-4">
      {best.length > 0 && <Days label="Best days" days={best} />}
      {careful.length > 0 && <Days label="Take it slow" days={careful} />}
    </div>
  );
}

/** Every area laid out the same way, one card each. */
interface AreaCardsProps {
  readonly areas: readonly WeekArea[];
  readonly period: ForecastPeriod;
  /** Called when someone opens a card's "Why". */
  readonly onWhyOpen?: () => void;
}

export function AreaCards({ areas, period, onWhyOpen }: AreaCardsProps) {
  return (
    <div className="mt-6 flex flex-col gap-3">
      {areas.map((area) => (
        <AreaCard key={area.key} area={area} period={period} onWhyOpen={onWhyOpen} />
      ))}
    </div>
  );
}

/**
 * One area: its tone, one line, a do and a don't. The planetary reasons fold
 * under a "Why" row that names the planets, so the card stays short and the
 * reason is one tap away, never hidden without a trace.
 */
function AreaCard({ area, period, onWhyOpen }: { readonly area: WeekArea; readonly period: ForecastPeriod; readonly onWhyOpen?: () => void }) {
  return (
    <section className="border-[1.5px] border-rule p-4" aria-label={area.title}>
      <div className="flex items-center justify-between gap-3">
        <h2 className="label text-ink">{area.title}</h2>
        <span
          className={`border-[1.5px] px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.14em] ${
            area.tone === "good" ? "border-ink bg-ink text-paper" : area.tone === "careful" ? "border-ink text-ink" : "border-rule text-mid"
          }`}
        >
          {toneLabel(area.tone, period)}
        </span>
      </div>
      <p className="mt-2 font-display text-[19px] leading-[1.35]">{area.line}</p>
      <dl className="mt-3 space-y-1.5 text-[15px] leading-[1.45]">
        <div className="flex gap-2">
          <dt className="w-12 shrink-0 font-mono text-[11px] uppercase tracking-[0.12em] leading-[1.9] text-mid">Do</dt>
          <dd>{area.do}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="w-12 shrink-0 font-mono text-[11px] uppercase tracking-[0.12em] leading-[1.9] text-mid">Don't</dt>
          <dd>{area.dont.replace(/^Don't\s+/i, "").replace(/^./, (c) => c.toUpperCase())}</dd>
        </div>
      </dl>
      {area.why.length > 0 && <Why reasons={area.why} onOpen={onWhyOpen} />}
    </section>
  );
}

/** One span per line ("Sun 11 to Thu 15 Oct"), so a month never reads as a wall of dates. */
function Why({ reasons, onOpen }: { readonly reasons: readonly string[]; readonly onOpen?: () => void }) {
  const planets = planetsIn(reasons);
  return (
    <details className="group mt-3 border-t border-rule" onToggle={(event) => event.currentTarget.open && onOpen?.()}>
      <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-3 pt-3 [&::-webkit-details-marker]:hidden">
        <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-mid">
          Why{planets && <span className="normal-case tracking-normal"> · {planets}</span>}
        </span>
        <span aria-hidden="true" className="font-mono text-[16px] leading-none text-ink group-open:hidden">
          +
        </span>
        <span aria-hidden="true" className="hidden font-mono text-[16px] leading-none text-ink group-open:inline">
          −
        </span>
      </summary>
      <div className="space-y-1.5 pt-2">
        {reasons.map((reason) => (
          <Tiny key={reason}>{reason}</Tiny>
        ))}
      </div>
    </details>
  );
}

function Days({ label, days }: { readonly label: string; readonly days: readonly string[] }) {
  return (
    <div>
      <Label>{label}</Label>
      <ul className="mt-2 space-y-1" aria-label={label}>
        {days.map((span) => (
          <li key={span} className="font-display text-[17px] leading-[1.35]">
            {span}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The honest footnote every reading carries. */
export function ReadingFootnote({ period }: { readonly period: ForecastPeriod }) {
  const when = period === "day" ? "today" : `this ${PERIOD_NOUN[period]}`;
  return (
    <Tiny className="mt-6">
      Worked out from your chart and where the planets are {when}, using the Lahiri system. The tradition
      reads these as tendencies, not certainties.
    </Tiny>
  );
}
